import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { KeyRound, ArrowRight } from "lucide-react";

/** Second login step for the owner account: authenticator or backup code. */
export function TwoFactorStep({ challenge, onDone, onRestart }) {
    const { verify2fa } = useAuth();
    const [code, setCode] = useState("");
    const [trust, setTrust] = useState(false);
    const [useBackup, setUseBackup] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [expired, setExpired] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            onDone(await verify2fa(challenge, code, trust));
        } catch (e2) {
            const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
            setErr(msg);
            if (/expired|too many/i.test(msg)) setExpired(true);
            setCode("");
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="twofa-form">
            <div className="w-11 h-11 rounded-md bg-primary/10 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-primary" />
            </div>
            <div>
                <div className="overline mb-2">Two-step login</div>
                <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">
                    {useBackup ? "Enter a backup code" : "Enter your 6-digit code"}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {useBackup ? "Each backup code works once." : "Open your authenticator app and type the code for WasteHQ."}
                </p>
            </div>
            <Input autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                   inputMode={useBackup ? "text" : "numeric"} autoComplete="one-time-code"
                   maxLength={useBackup ? 12 : 6} placeholder={useBackup ? "abcd-1234" : "123456"}
                   className="text-center text-2xl tracking-[0.4em] font-mono h-14" disabled={expired}
                   data-testid="twofa-code-input" />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={trust} onCheckedChange={(v) => setTrust(!!v)} data-testid="twofa-trust-checkbox" />
                Trust this device for 30 days
            </label>
            {err && (
                <div data-testid="twofa-error" className="text-xs text-red-600 dark:text-red-400 p-2 bg-red-500/10 border border-red-500/20 rounded-md">
                    {err}
                </div>
            )}
            {expired ? (
                <Button type="button" className="w-full" onClick={onRestart} data-testid="twofa-restart-btn">
                    Sign in again
                </Button>
            ) : (
                <Button type="submit" className="w-full" disabled={busy || code.length < 6} data-testid="twofa-submit-btn">
                    {busy ? "Checking…" : "Verify"}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
            )}
            <div className="flex justify-between text-xs">
                <button type="button" className="text-primary hover:underline" data-testid="twofa-toggle-backup"
                        onClick={() => { setUseBackup((v) => !v); setCode(""); setErr(""); }}>
                    {useBackup ? "Use authenticator code" : "Lost your phone? Use a backup code"}
                </button>
                <button type="button" className="text-muted-foreground hover:underline" onClick={onRestart}
                        data-testid="twofa-back-btn">Back</button>
            </div>
        </form>
    );
}
